import React from 'react';
import PropTypes from 'prop-types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES, MARKS } from '@contentful/rich-text-types';

import BlockQuote from '../BlockQuote';
import { Bold, Paragraph } from './styled';
import {
  TBody,
  Cell,
  HeaderCell,
  Row,
  Table,
  TableWrapper,
} from '../CommonStyledComponents';

function renderOptions(references, entity) {
  // create a map
  const map = (references || []).reduce(
    (acc, ref) => ({
      ...acc,
      [ref.contentful_id]: ref,
    }),
    {},
  );

  return {
    renderNode: {
      [MARKS.BOLD]: (node, children) => <Bold>{children}</Bold>,

      [BLOCKS.TABLE]: (node, children) => (
        <TableWrapper>
          <Table>
            <TBody>{children}</TBody>
          </Table>
        </TableWrapper>
      ),
      [BLOCKS.TABLE_CELL]: (node, children) => <Cell>{children}</Cell>,
      [BLOCKS.TABLE_ROW]: (node, children) => <Row>{children}</Row>,
      [BLOCKS.TABLE_HEADER_CELL]: (node, children) => (
        <HeaderCell>{children}</HeaderCell>
      ),

      [BLOCKS.PARAGRAPH]: (node, children) => <Paragraph>{children}</Paragraph>,

      [INLINES.HYPERLINK]: (node, children) => {
        const url = node.data.uri;
        const defaultLink = <a href={url}>{children}</a>;

        if (/youtu\.?be/.test(url)) {
          // Supported Youtube URL formats:
          // https://www.youtube.com/watch?v=VIDEO_ID
          // https://www.youtube.com/watch?v=VIDEO_ID&t=TIMESTAMP
          // https://youtu.be/VIDEO_ID
          // https://youtu.be/VIDEO_ID?t=TIMESTAMP
          const urlMatch = url.match(
            /^((https?:\/\/)?(www\.)?)?(youtube.com\/watch\?v=|youtu\.be\/)(?<video_id>[^&?]+)([&?]t=(?<timestamp>\d+)s)?$/,
          );

          if (!urlMatch) {
            return defaultLink;
          }

          const videoId = urlMatch.groups['video_id'];
          const timestamp = urlMatch.groups['timestamp'];

          // See supported query parameters here:
          // https://developers.google.com/youtube/player_parameters
          return (
            <iframe
              className="youtube-video"
              src={`https://www.youtube.com/embed/${videoId}?start=${
                timestamp || 0
              }`}
              title={children}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              frameBorder="0"
              allowFullScreen
            />
          );
        }

        return defaultLink;
      },

      [BLOCKS.EMBEDDED_ENTRY]: (node, children) => {
        const {
          data: {
            target: {
              sys: { id },
            },
          },
        } = node;
        const ref = map[id];

        if (!ref) {
          return children;
        }

        if (ref.__typename === 'ContentfulBlockQuote') {
          const { author, text } = ref;

          let quoteAuthor = author;

          if (!quoteAuthor) {
            quoteAuthor = `${entity.author}, ${entity.position}`;
          }

          return (
            <BlockQuote
              text={<RichTextToHtml richText={text} entity={entity} />}
              author={quoteAuthor}
            />
          );
        }

        if (ref.__typename === 'ContentfulImageWithLink') {
          const { url, title, image } = ref;

          return (
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="image-link"
            >
              <img src={image.resize.src} alt={title} />
            </a>
          );
        }

        return children;
      },

      [BLOCKS.EMBEDDED_ASSET]: (node, children) => {
        const {
          data: {
            target: {
              sys: { id },
            },
          },
        } = node;
        const ref = map[id];

        if (!ref) {
          return children;
        }

        if (/^image/.test(ref.mimeType)) {
          return <img src={ref.resize.src} alt={ref.description} />;
        }

        if (/^video/.test(ref.mimeType)) {
          return (
            <video width="590" controls src={ref.url}>
              {ref.description}
            </video>
          );
        }

        return children;
      },
    },
  };
}

const RichTextToHtml = (props) => {
  const { richText, entity } = props;
  const { raw, references } = richText;

  if (!raw) {
    return null;
  }

  let json;

  try {
    json = JSON.parse(raw);
  } catch (e) {
    console.error('Unable to parse JSON', e);
    return null;
  }

  return documentToReactComponents(json, renderOptions(references, entity));
};

RichTextToHtml.propTypes = {
  raw: PropTypes.string.isRequired,
  entity: PropTypes.object.isRequired,
};

export default RichTextToHtml;
